import { Accordion, AccordionItem } from "@heroui/accordion";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useCalculateExpectedScoreMutation,
  useCalculateKraScoreMutation,
  useGetManagementReviewQuery,
} from "../../../../api/employeeApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import bull from "../../../assets/bull.png";
import flag from "../../../assets/finish-FLAG.png";
import person from "../../../assets/personAppraisal.png";
import PageLayout from "../../../Components/Layout/PageLayout";
import { MetaData } from "../../Employee/SelfAppraisal/data";
import AppraisalReviewDetails from "../../Employee/SelfAppraisal/reviewDetails";
import { MeasurementTypeFormatter } from "../../HR/Appraisal/Modals/data";
export const HRReview = ({ id }) => {
  const { data, refetch } = useGetManagementReviewQuery(id);
  const allObjectives = useMemo(
    () => data?.responseObject?.objectives || [],
    [data?.responseObject?.objectives]
  );
  const employeeData = data?.responseObject?.employee;

  const initialized = React.useRef(false);
  const debounceRef = React.useRef(null);
  const debounceExpected = React.useRef(null);
  const [totalScore, setTotalScore] = useState({
    colorType: "",
    totalAppraisalScore: 0,
    performanceStatusLabelName: "",
  });
  const [maxScore, setMaxScore] = useState(0);

  const formMethods = useForm({
    mode: "all",
  });

  const [onCalculate] = useCalculateKraScoreMutation();
  const [expectedScore] = useCalculateExpectedScoreMutation();
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective, objIndex) => {
          const krAs = objective.krAs.map((kra, kraIndex) => {
            const actualScore =
              kra?.employeeReviewDetails?.find(
                (item) => item.appraisalType === 1
              )?.kraActualScore || 0;
            return {
              kraId: kra.kraId,
              actualValue: parseInt(actualScore) || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await onCalculate({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setTotalScore({
            colorType: res.colorType,
            totalAppraisalScore: Math.ceil(res.totalAppraisalScore),
            performanceStatusLabelName: res.performanceStatusLabelName,
          });
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [employeeData?.id, id, onCalculate, formMethods, allObjectives]);
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }

    if (!allObjectives.length) return;
    if (debounceExpected.current) {
      clearTimeout(debounceExpected.current);
    }
    debounceExpected.current = setTimeout(async () => {
      try {
        const objectives = allObjectives.map((objective) => {
          const krAs = objective.krAs.map((kra) => {
            return {
              kraId: kra.kraId,
              actualValue: kra.targetValue || 0,
              targetValue: kra.targetValue || 0.01,
              weight: kra.weight,
            };
          });
          return {
            objectiveId: objective.objectiveId,
            objectiveWeight: objective.objectiveWeight,
            krAs,
          };
        });

        const res = await expectedScore({
          employeeId: employeeData?.id || 0,
          referenceId: id,
          objectives,
        }).unwrap();
        if (res) {
          setMaxScore(res.totalAppraisalScore);
        }
      } catch (err) {
        console.error("Error calculating total actual score:", err);
      }
    }, 500);

    return () => {
      if (debounceExpected.current) {
        clearTimeout(debounceExpected.current);
      }
    };
  }, [employeeData?.id, id, expectedScore, formMethods, allObjectives]);

  return (
    <div>
      <div className={DashboardStyle.app_view_controls}></div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <p className="text-[#4B664C]">
            HR Comment:{" "}
            <span>{data?.responseObject?.hrComment ?? "N/A"}</span>
          </p>
          <p className="text-[#4B664C]">
            HR Recommendation:{" "}
            <span>{data?.responseObject?.hrRecommendation ?? "N/A"}</span>
          </p>
        </div>
        <span className={`text-[${totalScore.colorType}] font-[600]`}>
          Total Score: {totalScore.totalAppraisalScore}/{maxScore} (
          {totalScore.totalAppraisalScore / maxScore * 100 || 0})%
        </span>
      </div>
      <div>
        <div className={DashboardStyle.view_app_components}>
          <div className="mb-4"></div>
          <Accordion
            aria-label={`Accordion 1`}
            selectionMode="single"
            defaultExpandedKeys={["1"]}
            titleWrapper="shadow-md rounded-md border border-gray-200"
          >
            <AccordionItem
              title={
                <div className="flex items-center gap-5 px-5">
                  <img src={person} alt="" />{" "}
                  <span className="text-[#305F32] font-[600]">
                    EMPLOYEE PROFILE SUMMARY
                  </span>
                </div>
              }
              className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
            >
              <MetaData data={employeeData} />
            </AccordionItem>
          </Accordion>
          {allObjectives?.filter(
            (objective) =>
              objective?.objectiveType?.toLowerCase() === "personnel",
          )
            ?.map((item, index) => (
              <Accordion
                aria-label={`Accordion ${index + 1}`}
                key={index + 1}
                selectionMode="single"
                defaultExpandedKeys={["1"]}
                titleWrapper="shadow-md rounded-md border border-gray-200"
              >
                <AccordionItem
                  key={index + 1}
                  title={
                    <div className="flex items-center justify-between px-5">
                      <div className="flex items-center gap-5">
                        <img src={flag} alt="" />
                        <span className="text-[#4B664C] font-[600]">
                          OBJECTIVE: {item?.objectiveTitle}
                        </span>
                      </div>
                      <div>
                        <span className="text-[#4B664C] font-[600]">
                          Weight: {item?.objectiveWeight}%
                        </span>
                      </div>
                      <div className="w-10" />
                    </div>
                  }
                  className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                >
                  <div>
                    {item?.krAs?.map((items, kraIndex) => (
                      <Accordion
                        key={kraIndex + 1}
                        aria-label={`Accordion ${kraIndex + 1}`}
                        selectionMode="single"
                        defaultExpandedKeys={["1"]}
                        titleWrapper="shadow-md rounded-md border border-gray-200"
                      >
                        <AccordionItem
                          key={kraIndex + 1}
                          title={
                            <div className="flex items-center gap-5 px-5">
                              <img src={bull} alt="" />
                              <span className="text-[#4B664C] font-[600]">
                                KRA: {items?.kraTitle}
                              </span>
                            </div>
                          }
                          className="shadow-md rounded-md border border-gray-200 py-3 mb-2"
                        >
                          <div>
                            {items.isAutoGenerated && (
                              <div className="flex items-center justify-between px-5">
                                <div />
                                <div>
                                  <p className="text-[#4B664C] font-[600]">
                                    isAutogenerated
                                  </p>
                                </div>
                              </div>
                            )}
                            <div
                              key={kraIndex + 1}
                              className="flex items-center gap-5 py-2 px-5"
                              style={{
                                display: "grid",
                                gridTemplateColumns: "180px 1fr",
                                alignItems: "center",
                                gap: "16px",
                              }}
                            >
                              <h2 className="text-[#305F32] font-[600]">
                                Title: {items.kraTitle}
                              </h2>
                              <h4 className="text-[#305F32] font-[500]">
                                Description: {items.kraDescription}
                              </h4>
                              <h2 className="text-[#305F32] font-[600]">
                                Target: {items.kraTarget}
                              </h2>
                              <h4 className="text-[#00AB09] font-[500]">
                                Score: {items.kraScore || "Not scored yet"}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Measurement Type:{" "}
                                {MeasurementTypeFormatter(items.measurementType)}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Source: {items.source}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                KRA Type: {items.kraType}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Include Self Review:{" "}
                                {items.includeSelfReview ? "Yes" : "No"}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Period Start Date:{" "}
                                {new Date(
                                  items.periodStartDate
                                ).toLocaleDateString()}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Period End Date:{" "}
                                {new Date(
                                  items.periodEndDate
                                ).toLocaleDateString()}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                Next Due Date:{" "}
                                {new Date(items.nextDueDate).toLocaleDateString()}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                SLA Due Date:{" "}
                                {new Date(items.slaDueDate).toLocaleDateString()}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                SLA Reminder Count: {items.slaReminderCount}
                              </h4>
                              <h4 className="text-[#305F32] font-[500]">
                                SLA Start Duration: {items.slaStartDuration}
                              </h4>
                            </div>
                            {items?.employeeReviewDetails
                              ?.filter((dat) => dat?.appraisalType === 6)
                              ?.map((item) => (
                                <>
                                  {item?.comment && item?.kraActualScore && (
                                    <div className="bg-[#f9fbf7] rounded-xl border border-gray-200 p-6 w-full text-base">
                                      <AppraisalReviewDetails
                                        item={item}
                                        showDocument={item?.appraisalType === 6}
                                        peerReviewAffectsScore={
                                          items?.peerReviewAffectsScore
                                        }
                                        upwardReviewAffectsScore={
                                          items?.upwardReviewAffectsScore
                                        }
                                        crossReviewAffectsScore={
                                          items?.crossReviewAffectsScore
                                        }
                                      />
                                    </div>
                                  )}
                                </>
                              ))}
                          </div>
                        </AccordionItem>
                      </Accordion>
                    ))}
                  </div>
                </AccordionItem>
              </Accordion>
            ))}
        </div>
      </div>
    </div>
  );
};
